"use client"

import { useState, useCallback } from "react"
import { 
  Upload, 
  CheckCircle, 
  Download,
  Loader2,
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from "lucide-react"
import { payrollApi, type BulkProcessResponse, type PayrollType } from "@/lib/api"

type ProcessingStep = "select" | "upload" | "process" | "complete" | "error"

interface ProcessingState {
  currentStep: ProcessingStep
  uploadedFile: File | null
  employeeCount: number
  reportTypes: {
    payrollType: PayrollType
    statutoryReports: boolean
    individualPayslips: boolean
  }
  analytics: {
    totalGross: number
    totalNet: number
    totalPAYE: number
    totalNHIF: number
    totalNSSF: number
    totalHousingLevy: number
  }
  employees: BulkProcessResponse['employees']
  columns: string[],
  companyName: string
  payrollDate: string
  currentPage: number
  itemsPerPage: number
  searchQuery: string
  isProcessing: boolean
  isDownloadingReport: boolean
  isDownloadingPayslips: boolean
  error: string | null
}

export function BulkProcessingFlow() {
  const [state, setState] = useState<ProcessingState>({
    currentStep: "select",
    uploadedFile: null,
    employeeCount: 0,
    reportTypes: {
      payrollType: "SIMPLE",
      statutoryReports: false,
      individualPayslips: false
    },
    analytics: {
      totalGross: 0,
      totalNet: 0,
      totalPAYE: 0,
      totalNHIF: 0,
      totalNSSF: 0,
      totalHousingLevy: 0
    },
    employees: [],
    columns: [],
    companyName: '',
    payrollDate: '',
    currentPage: 1,
    itemsPerPage: 5,
    searchQuery: '',
    isProcessing: false,
    isDownloadingReport: false,
    isDownloadingPayslips: false,
    error: null
  })

  const handlePayrollTypeChange = useCallback((type: PayrollType) => {
    setState(prev => ({
      ...prev,
      reportTypes: { 
        ...prev.reportTypes, 
        payrollType: type,
        // Uncheck statutory reports when switching to Simple
        statutoryReports: type === "SIMPLE" ? false : prev.reportTypes.statutoryReports
      }
    }))
  }, [])

  const handleStatutoryReportChange = useCallback((checked: boolean) => {
    setState(prev => ({
      ...prev,
      reportTypes: {
        ...prev.reportTypes,
        statutoryReports: checked,
        // If statutory report is checked, switch to comprehensive
        payrollType: checked ? "COMPREHENSIVE" : prev.reportTypes.payrollType
      }
    }))
  }, [])

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const blob = await payrollApi.downloadTemplate(
        state.reportTypes.payrollType,
        state.reportTypes.statutoryReports
      )
      
      // Download the file
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `payroll_template_${state.reportTypes.payrollType.toLowerCase()}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      // Move to upload step after successful download
      setState(prev => ({ ...prev, currentStep: "upload" }))
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : "Failed to download template"
      }))
    }
  }, [state.reportTypes.payrollType, state.reportTypes.statutoryReports])

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setState(prev => ({
      ...prev,
      uploadedFile: file,
      currentStep: "process",
      isProcessing: true,
      error: null
    }))
    
    try {
      // Call API to process bulk payroll
      const result = await payrollApi.processBulkPayroll(
        file,
        state.reportTypes.payrollType,
        state.reportTypes.statutoryReports
      )
      
      setState(prev => ({
        ...prev,
        currentStep: "complete",
        isProcessing: false,
        employeeCount: result.employeeCount,
        analytics: result.analytics,
        employees: result.employees,
        columns: result.columns,
        companyName: result.companyName,
        payrollDate: result.payrollDate,
        error: null
      }))
    } catch (err) {
      setState(prev => ({
        ...prev,
        currentStep: "error",
        isProcessing: false,
        error: err instanceof Error ? err.message : "Failed to process payroll file"
      }))
    }
  }, [state.reportTypes.payrollType, state.reportTypes.statutoryReports])

  const handleDownloadReports = useCallback(async () => {
    if (state.employees.length === 0 || state.isDownloadingReport) return

    setState(prev => ({
      ...prev,
      isDownloadingReport: true,
      error: null
    }))

    try {
      const blob = await payrollApi.downloadReports(
        state.employees,
        state.analytics,
        state.reportTypes,
        state.companyName,
        state.payrollDate
      )
      
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `payroll_reports_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      setState(prev => ({
        ...prev,
        error: "Failed to download reports"
      }))
    } finally {
      setState(prev => ({
        ...prev,
        isDownloadingReport: false
      }))
    }
  }, [state.employees, state.analytics, state.reportTypes, state.isDownloadingReport, state.companyName, state.payrollDate])

  const handleDownloadPayslips = useCallback(async () => {
    if (state.employees.length === 0 || state.isDownloadingPayslips) return

    setState(prev => ({
      ...prev,
      isDownloadingPayslips: true,
      error: null
    }))

    try {
      const blob = await payrollApi.downloadPayslips(
        state.employees,
        state.analytics,
        state.reportTypes,
        state.companyName,
        state.payrollDate
      )
      
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `payroll_payslips_${new Date().toISOString().split('T')[0]}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      setState(prev => ({
        ...prev,
        error: "Failed to download payslips"
      }))
    } finally {
      setState(prev => ({
        ...prev,
        isDownloadingPayslips: false
      }))
    }
  }, [state.employees, state.analytics, state.reportTypes, state.isDownloadingPayslips, state.companyName, state.payrollDate])

  const handleReset = useCallback(() => {
    setState({
      currentStep: "select",
      uploadedFile: null,
      employeeCount: 0,
      reportTypes: {
        payrollType: "SIMPLE",
        statutoryReports: false,
        individualPayslips: false
      },
      analytics: {
        totalGross: 0,
        totalNet: 0,
        totalPAYE: 0,
        totalNHIF: 0,
        totalNSSF: 0,
        totalHousingLevy: 0
      },
      employees: [],
      columns: [],
      companyName: '',
      payrollDate: '',
      currentPage: 1,
      itemsPerPage: 5,
      searchQuery: '',
      isProcessing: false,
      isDownloadingReport: false,
      isDownloadingPayslips: false,
      error: null
    })
  }, [])

  const handleBackToSelect = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: "select", error: null }))
  }, [])

  return (
    <>
        {/* Select Reports Step */}
        {state.currentStep === "select" && (
          <div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {/* Payroll Type Selection */}
              <div>
                <div className="mono mb-4 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.13em] text-slate-400">
                  Payroll summary type
                </div>
                <div className="space-y-0">
                  <button
                    onClick={() => handlePayrollTypeChange("SIMPLE")}
                    className={`w-full border-b border-[#DCE3EA] py-4 text-left transition ${
                      state.reportTypes.payrollType === "SIMPLE"
                        ? "bg-[#F3F8FF]"
                        : "hover:bg-[#F3F6FA]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                        state.reportTypes.payrollType === "SIMPLE"
                          ? "border-[#2348C7] bg-[#2348C7]"
                          : "border-slate-300"
                      }`}>
                        {state.reportTypes.payrollType === "SIMPLE" && (
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        )}
                      </div>
                      <div>
                        <span className="text-[13px] font-medium text-slate-900">Simple Payroll Summary</span>
                        <p className="mt-0.5 text-[12px] text-slate-500">Basic salary breakdown and totals</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handlePayrollTypeChange("COMPREHENSIVE")}
                    className={`w-full py-4 text-left transition ${
                      state.reportTypes.payrollType === "COMPREHENSIVE"
                        ? "bg-[#F3F8FF]"
                        : "hover:bg-[#F3F6FA]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                        state.reportTypes.payrollType === "COMPREHENSIVE"
                          ? "border-[#2348C7] bg-[#2348C7]"
                          : "border-slate-300"
                      }`}>
                        {state.reportTypes.payrollType === "COMPREHENSIVE" && (
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        )}
                      </div>
                      <div>
                        <span className="text-[13px] font-medium text-slate-900">Comprehensive Payroll Summary</span>
                        <p className="mt-0.5 text-[12px] text-slate-500">Detailed breakdown with all deductions and allowances</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Additional Reports */}
              <div>
                <div className="mono mb-4 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.13em] text-slate-400">
                  Additional reports
                </div>
                <div className="space-y-0">
                  <button
                    onClick={() => setState(prev => ({
                      ...prev,
                      reportTypes: { ...prev.reportTypes, individualPayslips: !prev.reportTypes.individualPayslips }
                    }))}
                    className={`w-full border-b border-[#DCE3EA] py-4 text-left transition ${
                      state.reportTypes.individualPayslips
                        ? "bg-[#F3F8FF]"
                        : "hover:bg-[#F3F6FA]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-4 w-4 items-center justify-center rounded border ${
                        state.reportTypes.individualPayslips
                          ? "border-[#2348C7] bg-[#2348C7]"
                          : "border-slate-300"
                      }`}>
                        {state.reportTypes.individualPayslips && (
                          <CheckCircle className="h-3 w-3 text-white" strokeWidth={3} />
                        )}
                      </div>
                      <div>
                        <span className="text-[13px] font-medium text-slate-900">Individual Payslips</span>
                        <p className="mt-0.5 text-[12px] text-slate-500">Separate payslip for each employee</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleStatutoryReportChange(!state.reportTypes.statutoryReports)}
                    className={`w-full py-4 text-left transition ${
                      state.reportTypes.statutoryReports
                        ? "bg-[#F3F8FF]"
                        : "hover:bg-[#F3F6FA]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-4 w-4 items-center justify-center rounded border ${
                        state.reportTypes.statutoryReports
                          ? "border-[#2348C7] bg-[#2348C7]"
                          : "border-slate-300"
                      }`}>
                        {state.reportTypes.statutoryReports && (
                          <CheckCircle className="h-3 w-3 text-white" strokeWidth={3} />
                        )}
                      </div>
                      <div>
                        <span className="text-[13px] font-medium text-slate-900">Statutory Reports</span>
                        <p className="mt-0.5 text-[12px] text-slate-500">PAYE, SHIF, NSSF, and Housing Levy reports (requires Comprehensive)</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {state.error && (
              <div className="mt-5 flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {state.error}
              </div>
            )}

            <button
              onClick={handleDownloadTemplate}
              className="ink-button mt-8 flex w-full items-center justify-center gap-2 border border-slate-950 bg-slate-950 px-5 py-3 text-[13px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7]"
            >
              <Download className="h-4 w-4" />
              Download Template & Continue
            </button>
          </div>
        )}

        {/* Upload Step */}
        {state.currentStep === "upload" && (
          <div>
            {/* Selected reports summary */}
            <div className="border border-[#DCE3EA] bg-[#F3F6FA] px-5 py-4">
              <div className="mono mb-2 text-[9px] uppercase tracking-[.13em] text-slate-400">Selected reports</div>
              <ul className="space-y-1 text-[13px] text-slate-700">
                <li>• {state.reportTypes.payrollType === "SIMPLE" ? "Simple" : "Comprehensive"} Payroll Summary</li>
                {state.reportTypes.individualPayslips && <li>• Individual Payslips</li>}
                {state.reportTypes.statutoryReports && <li>• Statutory Reports</li>}
              </ul>
            </div>

            <div className="relative mt-6 border-2 border-dashed border-[#DCE3EA] bg-[#F3F6FA] p-10 text-center transition-colors hover:border-[#4B91F7]">
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileUpload}
                className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              />
              <Upload className="mx-auto mb-3 h-10 w-10 text-slate-400" />
              <p className="text-[13px] font-medium text-slate-900">Drop your file here or click to browse</p>
              <p className="mono mt-1 text-[10px] uppercase tracking-[.1em] text-slate-400">Supported format: .xlsx (Excel)</p>
            </div>

            <button
              onClick={handleBackToSelect}
              className="ink-button mt-6 border border-[#DCE3EA] px-5 py-2.5 text-[13px] font-medium text-slate-600 transition hover:bg-[#F3F6FA]"
            >
              ← Back
            </button>
          </div>
        )}

        {/* Processing */}
        {state.currentStep === "process" && state.isProcessing && (
          <div className="py-16 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center">
              <Loader2 className="h-10 w-10 animate-spin text-[#2348C7]" />
            </div>
            <h3 className="font-[family-name:var(--font-source-serif)] text-xl font-medium text-slate-950">Processing Payroll</h3>
            <p className="mono mt-2 text-[10px] uppercase tracking-[.13em] text-slate-400">Uploading file and calculating salaries…</p>
          </div>
        )}

        {/* Error */}
        {state.currentStep === "error" && (
          <div className="py-16 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center border border-red-200 bg-red-50">
              <AlertCircle className="h-7 w-7 text-red-500" />
            </div>
            <h3 className="text-[15px] font-medium text-slate-950">Processing Failed</h3>
            <p className="mt-2 text-[13px] text-red-600">{state.error}</p>
            <button
              onClick={handleReset}
              className="ink-button mt-6 inline-flex items-center gap-2 border border-slate-950 bg-slate-950 px-5 py-2.5 text-[13px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Try Again
            </button>
          </div>
        )}

        {/* Complete */}
        {state.currentStep === "complete" && (
          <div>
            {/* Analytics Summary */}
            <div className="square-card border border-[#DCE3EA] bg-white">
              <div className="flex items-center justify-between border-b border-[#DCE3EA] px-5 py-3">
                <div>
                  <span className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">Payroll summary</span>
                  <p className="mt-0.5 text-[12px] text-slate-500">{state.companyName} · {state.payrollDate}</p>
                </div>
                <span className="mono inline-flex items-center gap-1 text-[10px] uppercase text-[#0F7B4D]">
                  <CheckCircle className="h-3 w-3" />
                  Processed
                </span>
              </div>

              <div className="grid grid-cols-3 border-b border-[#DCE3EA]">
                <div className="border-r border-[#DCE3EA] px-5 py-4">
                  <span className="mono block text-[9px] uppercase tracking-[.1em] text-slate-400">Total gross</span>
                  <strong className="mt-1.5 block text-lg font-medium">KSh {state.analytics.totalGross.toLocaleString()}</strong>
                </div>
                <div className="border-r border-[#DCE3EA] px-5 py-4">
                  <span className="mono block text-[9px] uppercase tracking-[.1em] text-slate-400">Total net</span>
                  <strong className="mt-1.5 block text-lg font-medium text-[#0F7B4D]">KSh {state.analytics.totalNet.toLocaleString()}</strong>
                </div>
                <div className="px-5 py-4">
                  <span className="mono block text-[9px] uppercase tracking-[.1em] text-slate-400">Employees</span>
                  <strong className="mt-1.5 block text-lg font-medium">{state.employeeCount.toLocaleString()}</strong>
                </div>
              </div>

              <div className="px-5 py-4">
                <div className="mono mb-2 grid grid-cols-[1fr_100px] gap-3 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.08em] text-slate-400">
                  <span>Statutory deduction</span>
                  <span className="text-right">Total</span>
                </div>
                <div className="text-[13px] text-slate-700">
                  <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-3">
                    <span>PAYE</span>
                    <span className="mono text-right font-medium text-red-500">KSh {state.analytics.totalPAYE.toLocaleString()}</span>
                  </div>
                  <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-3">
                    <span>NSSF</span>
                    <span className="mono text-right font-medium text-red-500">KSh {state.analytics.totalNSSF.toLocaleString()}</span>
                  </div>
                  <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-3">
                    <span>SHIF</span>
                    <span className="mono text-right font-medium text-red-500">KSh {state.analytics.totalNHIF.toLocaleString()}</span>
                  </div>
                  <div className="grid grid-cols-[1fr_100px] gap-3 py-3">
                    <span>Housing Levy</span>
                    <span className="mono text-right font-medium text-red-500">KSh {state.analytics.totalHousingLevy.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Data Table */}
            <div className="mt-8">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span className="mono text-[9px] uppercase tracking-[.13em] text-slate-400">Employee payroll data</span>
                <input
                  type="text"
                  placeholder="Search employees…"
                  value={state.searchQuery}
                  onChange={(e) => setState(prev => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))}
                  className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[13px] text-slate-900 outline-none placeholder:text-slate-300 focus:border-[#4B91F7] sm:w-56"
                />
              </div>

              <div className="square-card overflow-hidden border border-[#DCE3EA]">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="mono border-b border-[#DCE3EA] bg-[#F3F6FA] text-left text-[9px] uppercase tracking-[.08em] text-slate-400">
                        {state.columns.map((column, index) => (
                          <th key={index} className="whitespace-nowrap px-5 py-3 font-normal">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filteredEmployees = state.employees.filter(employee =>
                          state.columns.some(column =>
                            String(employee[column]).toLowerCase().includes(state.searchQuery.toLowerCase())
                          )
                        );
                        return filteredEmployees
                          .slice(
                            (state.currentPage - 1) * state.itemsPerPage,
                            state.currentPage * state.itemsPerPage
                          )
                          .map((employee, rowIndex) => (
                            <tr key={rowIndex} className="border-b border-[#E8E7E1] text-[13px] text-slate-700 transition hover:bg-[#F3F6FA]">
                              {state.columns.map((column, colIndex) => (
                                <td key={colIndex} className="whitespace-nowrap px-5 py-3">
                                  {typeof employee[column] === 'number' ? (
                                    <span className="mono font-medium">{employee[column].toLocaleString()}</span>
                                  ) : (
                                    employee[column]
                                  )}
                                </td>
                              ))}
                            </tr>
                          ));
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex flex-col gap-3 border-t border-[#DCE3EA] bg-[#F3F6FA] px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-[12px] text-slate-500">
                      {(() => {
                        const filteredCount = state.employees.filter(employee =>
                          state.columns.some(column =>
                            String(employee[column]).toLowerCase().includes(state.searchQuery.toLowerCase())
                          )
                        ).length;
                        return (
                          <>
                            {filteredCount > 0 ? (state.currentPage - 1) * state.itemsPerPage + 1 : 0}–{Math.min(state.currentPage * state.itemsPerPage, filteredCount)} of {filteredCount}
                            {state.searchQuery && ` (filtered from ${state.employees.length})`}
                          </>
                        );
                      })()}
                    </span>
                    <select
                      value={state.itemsPerPage}
                      onChange={(e) => setState(prev => ({ ...prev, itemsPerPage: Number(e.target.value), currentPage: 1 }))}
                      className="border border-[#DCE3EA] bg-white px-2 py-1 text-[11px] text-slate-600 outline-none focus:border-[#4B91F7]"
                    >
                      <option value={5}>5 / page</option>
                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setState(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                      disabled={state.currentPage === 1}
                      className="inline-flex items-center gap-1 border border-[#DCE3EA] px-3 py-1.5 text-[11px] font-medium text-slate-600 transition hover:bg-white disabled:opacity-40"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      Previous
                    </button>
                    <button
                      onClick={() => setState(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                      disabled={(() => {
                        const filteredCount = state.employees.filter(employee =>
                          state.columns.some(column =>
                            String(employee[column]).toLowerCase().includes(state.searchQuery.toLowerCase())
                          )
                        ).length;
                        return state.currentPage >= Math.ceil(filteredCount / state.itemsPerPage);
                      })()}
                      className="inline-flex items-center gap-1 border border-[#DCE3EA] px-3 py-1.5 text-[11px] font-medium text-slate-600 transition hover:bg-white disabled:opacity-40"
                    >
                      Next
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleReset}
                className="ink-button flex-1 border border-[#DCE3EA] px-5 py-3 text-[13px] font-medium text-slate-600 transition hover:bg-[#F3F6FA]"
              >
                Process Another
              </button>
              {state.reportTypes.individualPayslips && (
                <button
                  onClick={handleDownloadPayslips}
                  disabled={state.isDownloadingPayslips}
                  className="ink-button flex-1 border border-slate-950 bg-slate-950 px-5 py-3 text-[13px] font-medium text-white transition hover:border-slate-800 hover:bg-slate-800 disabled:opacity-40"
                >
                  {state.isDownloadingPayslips ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Downloading…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Download className="h-4 w-4" />
                      Download Payslips (PDF)
                    </span>
                  )}
                </button>
              )}
              <button
                onClick={handleDownloadReports}
                disabled={state.isDownloadingReport}
                className="ink-button flex-1 border border-[#2348C7] bg-[#2348C7] px-5 py-3 text-[13px] font-medium text-white transition hover:bg-[#18369B] disabled:opacity-40"
              >
                {state.isDownloadingReport ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Downloading…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Download className="h-4 w-4" />
                    Download Report
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
    </>
  )
}
